import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { HotToastService } from '@ngxpert/hot-toast';
import { Observable } from 'rxjs';
/**
 * The single seam between this application and its toast library.
 *
 * Nothing outside this file may import from '@ngxpert/hot-toast'. The 349 call
 * sites inject this service instead, so changing toast library is a change to
 * one class rather than a workspace-wide edit.
 *
 * That already paid off once. The original library, '@ngneat/hot-toast', is
 * abandoned: its last release declares a peer range of '>= 17.0.0 < 18.0.0'
 * and blocked every Angular upgrade past 17. '@ngxpert/hot-toast' is the
 * maintained fork of the same codebase, published one major per Angular
 * version - 2.x for Angular 17, 3.x for 18, 4.x for 19, 5.x for 20 - so it
 * must be bumped in step with each Angular upgrade.
 *
 * Method names, argument order and return types deliberately mirror
 * HotToastService, which is why no call site changed when this was introduced.
 */
/** Toast body. Matches hot-toast's `Content`: text, or a template/component. */
export type NotificationContent = unknown;
/**
 * Per-toast overrides. Structural on purpose: it accepts the option objects
 * already passed at call sites (`duration`, `dismissible`, `style`, ...)
 * without re-exporting the library's own type.
 */
export interface NotificationOptions {
  id?: string;
  duration?: number;
  dismissible?: boolean;
  autoClose?: boolean;
  className?: string;
  position?: string;
  style?: Record<string, string>;
  icon?: unknown;
  [key: string]: unknown;
}
/** Messages for {@link NotificationService.observe}. */
export interface ObserveMessages<T> {
  loading?: NotificationContent;
  success?: NotificationContent | ((value: T) => NotificationContent);
  error?: NotificationContent | ((error: any) => NotificationContent);
}
/** Handle to a shown toast, so callers can close or update it. */
export type NotificationRef = ReturnType<HotToastService['show']>;
@Injectable({ providedIn: 'root' })
export class NotificationService {
  // The only reference to the toast library in application code.
  private readonly toast = inject(HotToastService);
  private readonly transloco = inject(TranslocoService);
  success(message?: NotificationContent, options?: NotificationOptions): NotificationRef {
    return this.toast.success(this.translate(message) as any, options as any);
  }
  error(message?: NotificationContent, options?: NotificationOptions): NotificationRef {
    return this.toast.error(this.translate(message) as any, options as any);
  }
  warning(message?: NotificationContent, options?: NotificationOptions): NotificationRef {
    return this.toast.warning(this.translate(message) as any, options as any);
  }
  info(message?: NotificationContent, options?: NotificationOptions): NotificationRef {
    return this.toast.info(this.translate(message) as any, options as any);
  }
  loading(message?: NotificationContent, options?: NotificationOptions): NotificationRef {
    return this.toast.loading(this.translate(message) as any, options as any);
  }
  show(message?: NotificationContent, options?: NotificationOptions): NotificationRef {
    return this.toast.show(this.translate(message) as any, options as any);
  }
  /**
   * RxJS operator that shows a loading toast while the source is pending, then
   * replaces it with the success or error message.
   */
  observe<T>(messages: ObserveMessages<T>): (source: Observable<T>) => Observable<T> {
    return this.toast.observe<T, unknown>(messages as any);
  }
  /** Close one toast by id, or all of them when no id is given. */
  close(id?: string): void {
    this.toast.close(id);
  }
  /**
   * Plain-text messages are dictionary keys (the English text itself), so they
   * follow the active language. Unknown text, such as API error messages,
   * passes through unchanged via the fallback/missing handler.
   */
  private translate(message?: NotificationContent): NotificationContent {
    return typeof message === 'string' && message.trim()
      ? this.transloco.translate(message)
      : message;
  }
}
